'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RentVsBuyCalculator() {
  const t = useTranslations("tools.rentVsBuyCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    homePrice: 500000,
    downPayment: 20,
    interestRate: 6.286,
    loanTerm: 30,
    buyingClosing: 2,
    propertyTax: 1.5,
    propertyTaxIncrease: 3,
    homeInsurance: 2500,
    hoaFee: 0,
    maintenance: 1.5,
    homeAppreciation: 3,
    costIncrease: 3,
    sellingClosing: 7,
    monthlyRent: 3000,
    rentIncrease: 3,
    renterInsurance: 15,
    securityDeposit: 3000,
    upfrontCost: 100,
    investmentReturn: 5,
    federalTax: 25,
    stateTax: 0,
    filingStatus: 'married'
  });

  const [results, setResults] = useState<any>(null);

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      margin: '-1.5rem -1rem 1.5rem -1rem',
      padding: '2rem'
    },
    toolTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formSection: getResponsiveStyle(
      {
        background: '#f8fafc',
        borderRadius: '0.75rem',
        border: '2px solid #e2e8f0'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    sectionTitle: getResponsiveStyle(
      {
        margin: '0 0 1.5rem 0',
        color: '#1e293b',
        fontWeight: 600,
        borderBottom: '2px solid #2563eb',
        paddingBottom: '0.5rem'
      },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#475569',
      fontWeight: 500,
      fontSize: '0.9rem'
    },
    inputWrapper: {
      position: 'relative' as const,
      width: '100%'
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem' },
      { padding: '0.875rem' },
      { padding: '0.75rem' }
    ),
    inputSuffix: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontSize: '0.9rem',
      pointerEvents: 'none' as const
    },
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem' },
      { padding: '0.875rem' },
      { padding: '0.75rem' }
    ),
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' },
      { flexDirection: 'column' as const, gridColumn: '1' },
      { flexDirection: 'row' as const, gridColumn: '1 / -1' },
      { flexDirection: 'row' as const, gridColumn: '1 / -1' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        fontWeight: 600,
        border: 'none',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
      },
      { padding: '1rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' }
    ),
    resetBtn: getResponsiveStyle(
      {
        fontWeight: 600,
        border: 'none',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: '#475569'
      },
      { padding: '1rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' }
    ),
    resultsSection: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderTop: '3px solid #22c55e',
        borderRadius: '0 0 1rem 1rem',
        margin: '0 -1rem -1.5rem -1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: '#166534', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    recommendation: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        marginBottom: '2rem',
        textAlign: 'center' as const,
        border: '2px solid #22c55e'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    recommendationText: getResponsiveStyle(
      { color: '#1e293b', margin: 0 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    recommendationHighlight: getResponsiveStyle(
      { fontWeight: 700, color: '#16a34a', margin: '0.5rem 0' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    chartContainer: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        marginBottom: '2rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    comparisonGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginTop: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    comparisonCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        border: '2px solid #e2e8f0'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    comparisonTitle: {
      margin: '0 0 1rem 0',
      color: '#1e293b',
      fontSize: '1.1rem',
      fontWeight: 600
    },
    costItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f1f5f9'
    },
    costLabel: {
      color: '#64748b'
    },
    costValue: {
      color: '#1e293b',
      fontWeight: 500
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginBottom: '1rem', paddingLeft: '1.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    reference: {
      marginBottom: '0.75rem'
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const
    }
  };

  const calculateMortgagePayment = (principal: number, rate: number, years: number) => {
    const monthlyRate = rate / 100 / 12;
    const numPayments = years * 12;
    if (monthlyRate === 0) return principal / numPayments;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  };

  const calculateYearlyInterest = (principal: number, rate: number, term: number, year: number) => {
    const monthlyRate = rate / 100 / 12;
    const monthlyPayment = calculateMortgagePayment(principal, rate, term);

    let balance = principal;
    let yearlyInterest = 0;
    const startMonth = (year - 1) * 12 + 1;
    const endMonth = year * 12;

    for (let month = 1; month <= endMonth; month++) {
      const interest = balance * monthlyRate;
      const principalPayment = monthlyPayment - interest;

      if (month >= startMonth) {
        yearlyInterest += interest;
      }

      balance -= principalPayment;
      if (balance < 0) balance = 0;
    }

    return yearlyInterest;
  };

  const calculateBuyingCosts = (inputs: any, years: number) => {
    const homePrice = inputs.homePrice;
    const downPaymentAmount = homePrice * inputs.downPayment / 100;
    const loanAmount = homePrice - downPaymentAmount;
    const monthlyMortgage = calculateMortgagePayment(loanAmount, inputs.interestRate, inputs.loanTerm);

    let totalCost = 0;
    let currentHomeValue = homePrice;
    let currentPropertyTax = homePrice * inputs.propertyTax / 100;
    let currentInsurance = inputs.homeInsurance;
    let currentMaintenance = homePrice * inputs.maintenance / 100;
    let currentHOA = inputs.hoaFee;

    totalCost += downPaymentAmount;
    totalCost += homePrice * inputs.buyingClosing / 100;

    const yearlyData = [];

    for (let year = 1; year <= years; year++) {
      let yearCost = monthlyMortgage * 12;
      yearCost += currentPropertyTax;
      yearCost += currentInsurance;
      yearCost += currentMaintenance;
      yearCost += currentHOA;

      const interestPaid = calculateYearlyInterest(loanAmount, inputs.interestRate, inputs.loanTerm, year);
      const taxDeduction = (interestPaid + currentPropertyTax) * (inputs.federalTax + inputs.stateTax) / 100;
      yearCost -= taxDeduction;

      totalCost += yearCost;

      currentHomeValue *= (1 + inputs.homeAppreciation / 100);
      currentPropertyTax *= (1 + inputs.propertyTaxIncrease / 100);
      currentInsurance *= (1 + inputs.costIncrease / 100);
      currentMaintenance *= (1 + inputs.costIncrease / 100);
      currentHOA *= (1 + inputs.costIncrease / 100);

      yearlyData.push({
        year: year,
        avgMonthlyCost: totalCost / (year * 12)
      });
    }

    const sellingCosts = currentHomeValue * inputs.sellingClosing / 100;
    const netProceeds = currentHomeValue - sellingCosts - loanAmount;
    totalCost -= netProceeds;

    return {
      totalCost: totalCost,
      yearlyData: yearlyData,
      monthlyMortgage: monthlyMortgage,
      downPayment: downPaymentAmount,
      closingCosts: homePrice * inputs.buyingClosing / 100
    };
  };

  const calculateRentingCosts = (inputs: any, years: number) => {
    let totalCost = 0;
    let currentRent = inputs.monthlyRent;
    let currentInsurance = inputs.renterInsurance;

    totalCost += inputs.securityDeposit;
    totalCost += inputs.upfrontCost;

    const yearlyData = [];

    for (let year = 1; year <= years; year++) {
      const yearCost = (currentRent + currentInsurance) * 12;
      totalCost += yearCost;

      yearlyData.push({
        year: year,
        avgMonthlyCost: totalCost / (year * 12)
      });

      currentRent *= (1 + inputs.rentIncrease / 100);
      currentInsurance *= (1 + inputs.rentIncrease / 100);
    }

    totalCost -= inputs.securityDeposit;

    return {
      totalCost: totalCost,
      yearlyData: yearlyData,
      monthlyRent: inputs.monthlyRent,
      renterInsurance: inputs.renterInsurance
    };
  };

  const findBreakevenYear = (buyData: any[], rentData: any[]) => {
    for (let i = 0; i < buyData.length; i++) {
      if (buyData[i].avgMonthlyCost < rentData[i].avgMonthlyCost) {
        if (i === 0) return 1;

        const prevDiff = rentData[i-1].avgMonthlyCost - buyData[i-1].avgMonthlyCost;
        const currDiff = buyData[i].avgMonthlyCost - rentData[i].avgMonthlyCost;
        const fraction = prevDiff / (prevDiff + currDiff);

        return (i + fraction).toFixed(1);
      }
    }
    return null;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const buyResults = calculateBuyingCosts(inputs, 30);
    const rentResults = calculateRentingCosts(inputs, 30);
    const breakevenYear = findBreakevenYear(buyResults.yearlyData, rentResults.yearlyData);

    setResults({
      buy: buyResults,
      rent: rentResults,
      breakeven: breakevenYear
    });
  };

  const handleReset = () => {
    setInputs({
      homePrice: 500000,
      downPayment: 20,
      interestRate: 6.286,
      loanTerm: 30,
      buyingClosing: 2,
      propertyTax: 1.5,
      propertyTaxIncrease: 3,
      homeInsurance: 2500,
      hoaFee: 0,
      maintenance: 1.5,
      homeAppreciation: 3,
      costIncrease: 3,
      sellingClosing: 7,
      monthlyRent: 3000,
      rentIncrease: 3,
      renterInsurance: 15,
      securityDeposit: 3000,
      upfrontCost: 100,
      investmentReturn: 5,
      federalTax: 25,
      stateTax: 0,
      filingStatus: 'married'
    });
    setResults(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.toolTitle}>{t("toolTitle")}</h2>
        </div>

        <form onSubmit={handleCalculate}>
          <div style={styles.formGrid}>
            <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>{t("homePurchaseSection")}</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("homePrice")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.homePrice}
                    onChange={(e) => handleInputChange('homePrice', parseFloat(e.target.value))}
                    min="0"
                    step="1000"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("downPayment")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.downPayment}
                    onChange={(e) => handleInputChange('downPayment', parseFloat(e.target.value))}
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("interestRate")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.interestRate}
                    onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.001"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("loanTerm")}</label>
                <input
                  type="number"
                  style={styles.input}
                  value={inputs.loanTerm}
                  onChange={(e) => handleInputChange('loanTerm', parseInt(e.target.value))}
                  min="1"
                  max="50"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("buyingClosing")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.buyingClosing}
                    onChange={(e) => handleInputChange('buyingClosing', parseFloat(e.target.value))}
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("propertyTax")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.propertyTax}
                    onChange={(e) => handleInputChange('propertyTax', parseFloat(e.target.value))}
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("propertyTaxIncrease")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.propertyTaxIncrease}
                    onChange={(e) => handleInputChange('propertyTaxIncrease', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("homeInsurance")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.homeInsurance}
                    onChange={(e) => handleInputChange('homeInsurance', parseFloat(e.target.value))}
                    min="0"
                    step="100"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("hoaFee")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.hoaFee}
                    onChange={(e) => handleInputChange('hoaFee', parseFloat(e.target.value))}
                    min="0"
                    step="100"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("maintenance")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.maintenance}
                    onChange={(e) => handleInputChange('maintenance', parseFloat(e.target.value))}
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("homeAppreciation")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.homeAppreciation}
                    onChange={(e) => handleInputChange('homeAppreciation', parseFloat(e.target.value))}
                    min="-10"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("costIncrease")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.costIncrease}
                    onChange={(e) => handleInputChange('costIncrease', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("sellingClosing")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.sellingClosing}
                    onChange={(e) => handleInputChange('sellingClosing', parseFloat(e.target.value))}
                    min="0"
                    max="15"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>
            </div>

            <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>{t("homeRentSection")}</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("monthlyRent")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.monthlyRent}
                    onChange={(e) => handleInputChange('monthlyRent', parseFloat(e.target.value))}
                    min="0"
                    step="50"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("rentIncrease")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.rentIncrease}
                    onChange={(e) => handleInputChange('rentIncrease', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("renterInsurance")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.renterInsurance}
                    onChange={(e) => handleInputChange('renterInsurance', parseFloat(e.target.value))}
                    min="0"
                    step="5"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("securityDeposit")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.securityDeposit}
                    onChange={(e) => handleInputChange('securityDeposit', parseFloat(e.target.value))}
                    min="0"
                    step="100"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("upfrontCost")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.upfrontCost}
                    onChange={(e) => handleInputChange('upfrontCost', parseFloat(e.target.value))}
                    min="0"
                    step="50"
                    required
                  />
                  <span style={styles.inputSuffix}>$</span>
                </div>
              </div>

              <h3 style={styles.sectionTitle}>{t("yourInfoSection")}</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("investmentReturn")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.investmentReturn}
                    onChange={(e) => handleInputChange('investmentReturn', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("federalTax")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.federalTax}
                    onChange={(e) => handleInputChange('federalTax', parseFloat(e.target.value))}
                    min="0"
                    max="50"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("stateTax")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    style={styles.input}
                    value={inputs.stateTax}
                    onChange={(e) => handleInputChange('stateTax', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("filingStatus")}</label>
                <select
                  style={styles.select}
                  value={inputs.filingStatus}
                  onChange={(e) => handleInputChange('filingStatus', e.target.value)}
                >
                  <option value="single">{t("single")}</option>
                  <option value="married">{t("married")}</option>
                  <option value="marriedSeparate">{t("marriedSeparate")}</option>
                  <option value="head">{t("head")}</option>
                  <option value="widow">{t("widow")}</option>
                </select>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button type="submit" style={styles.calculateBtn}>
                {t("calculateButton")}
              </button>
              <button type="button" style={styles.resetBtn} onClick={handleReset}>
                {t("resetButton")}
              </button>
            </div>
          </div>
        </form>

        {results && (
          <div style={styles.resultsSection}>
            <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>

            <div style={styles.recommendation}>
              <p style={styles.recommendationText}>
                {results.breakeven && parseFloat(results.breakeven) <= 30
                  ? t("buyingCheaperIf")
                  : t("rentingCheaper")}
              </p>
              <p style={styles.recommendationHighlight}>
                {results.breakeven && parseFloat(results.breakeven) <= 30
                  ? `${results.breakeven} ${t("yearsOrLonger")}`
                  : t("considerRenting")}
              </p>
            </div>

            <div style={styles.comparisonGrid}>
              <div style={styles.comparisonCard}>
                <h4 style={styles.comparisonTitle}>{t("buyingCosts30Years")}</h4>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("downPaymentLabel")}:</span>
                  <span style={styles.costValue}>
                    ${results.buy.downPayment.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("monthlyMortgageLabel")}:</span>
                  <span style={styles.costValue}>
                    ${results.buy.monthlyMortgage.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("closingCostsLabel")}:</span>
                  <span style={styles.costValue}>
                    ${results.buy.closingCosts.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div style={{...styles.costItem, borderBottom: 'none', fontWeight: 600, fontSize: '1.1rem', color: '#2563eb'}}>
                  <span style={styles.costLabel}>{t("totalCost30Years")}:</span>
                  <span style={styles.costValue}>
                    ${results.buy.totalCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>

              <div style={styles.comparisonCard}>
                <h4 style={styles.comparisonTitle}>{t("rentingCosts30Years")}</h4>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("monthlyRentLabel")}:</span>
                  <span style={styles.costValue}>
                    ${results.rent.monthlyRent.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("renterInsuranceLabel")}:</span>
                  <span style={styles.costValue}>
                    ${results.rent.renterInsurance.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo
                  </span>
                </div>
                <div style={styles.costItem}>
                  <span style={styles.costLabel}>{t("securityDepositLabel")}:</span>
                  <span style={styles.costValue}>
                    ${inputs.securityDeposit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div style={{...styles.costItem, borderBottom: 'none', fontWeight: 600, fontSize: '1.1rem', color: '#2563eb'}}>
                  <span style={styles.costLabel}>{t("totalCost30Years")}:</span>
                  <span style={styles.costValue}>
                    ${results.rent.totalCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li>{t("howToStep1")}</li>
          <li>{t("howToStep2")}</li>
          <li>{t("howToStep3")}</li>
          <li>{t("howToStep4")}</li>
          <li>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <h3 style={styles.h3}>{t("insights2025Title")}</h3>
        <p style={styles.paragraph}>{t("insights2025Para1")}</p>
        <p style={styles.paragraph}>{t("insights2025Para2")}</p>

        <h3 style={styles.h3}>{t("insightsKeyFactorsTitle")}</h3>
        <ul style={styles.list}>
          <li>{t("insightsFactor1")}</li>
          <li>{t("insightsFactor2")}</li>
          <li>{t("insightsFactor3")}</li>
          <li>{t("insightsFactor4")}</li>
        </ul>

        <h3 style={styles.h3}>{t("insightsRegionalTitle")}</h3>
        <p style={styles.paragraph}>{t("insightsRegionalPara")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>

        <h3 style={styles.h3}>{t("detailsBuyingTitle")}</h3>
        <ul style={styles.list}>
          <li><strong>{t("detailsMortgage")}:</strong> {t("detailsMortgageDesc")}</li>
          <li><strong>{t("detailsPropertyTax")}:</strong> {t("detailsPropertyTaxDesc")}</li>
          <li><strong>{t("detailsInsurance")}:</strong> {t("detailsInsuranceDesc")}</li>
          <li><strong>{t("detailsMaintenance")}:</strong> {t("detailsMaintenanceDesc")}</li>
          <li><strong>{t("detailsClosingCosts")}:</strong> {t("detailsClosingCostsDesc")}</li>
          <li><strong>{t("detailsAppreciation")}:</strong> {t("detailsAppreciationDesc")}</li>
        </ul>

        <h3 style={styles.h3}>{t("detailsRentingTitle")}</h3>
        <ul style={styles.list}>
          <li><strong>{t("detailsRent")}:</strong> {t("detailsRentDesc")}</li>
          <li><strong>{t("detailsRenterInsurance")}:</strong> {t("detailsRenterInsuranceDesc")}</li>
          <li><strong>{t("detailsFlexibility")}:</strong> {t("detailsFlexibilityDesc")}</li>
          <li><strong>{t("detailsNoMaintenance")}:</strong> {t("detailsNoMaintenanceDesc")}</li>
        </ul>

        <h3 style={styles.h3}>{t("detailsOpportunityCostTitle")}</h3>
        <p style={styles.paragraph}>{t("detailsOpportunityCostDesc")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.paragraph}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.paragraph}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.paragraph}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.paragraph}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.paragraph}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.paragraph}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <div style={styles.reference}>
          <a href="https://www.realtor.com/research/june-2025-rent/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("ref1")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.bankrate.com/real-estate/rent-vs-buy-affordability-study/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("ref2")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.nerdwallet.com/mortgages/calculators/rent-vs-buy-calculator" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("ref3")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.zillow.com/rent-vs-buy-calculator" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("ref4")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.empower.com/the-currency/life/money/rent-vs-buy-2025-top-50-metros-news" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("ref5")}
          </a>
        </div>
      </section>
    </div>
  );
}